import React from "react";
import { Chip, ChipProps } from "../Chip";
import { TooltipContainer } from "../Tooltip.jsx";
import { TrialLearnMoreDialog } from "../StarterTrial/TrialLearnMoreDialog";
import { UpgradeModal } from "../UpgradeModal/UpgradeModal";
import GatedFeatureModal from "../UpgradeModal/GatedFeatureModal";
import { useStore } from "zustand";
import globalStore from "../../../../state";
const { isTrial } = require("../../../routes/workspace/utils");
import {
    isFeatureAvailable,
    Feature,
    isValidFeatureId,
    isTrialFeature,
    isPremiumFeature,
    isUpsellableFeature
} from "@functions/featureAvailability";
import { Plan } from "@functions/plans/types";
import { twClassNames } from "../../helpers/twClassNames";
import { getIconForWorkspacePlan } from "../../../routes/workspace/utils";
import usePremiumModalStore from "../PremiumFeatureModal/store";
import { openUpsellFeatureModal } from "./upsellUtilities";

interface BaseFeatureIndicatorProps {
    featureId: Feature;
    showTextInChip: boolean;
    chipTextContent: string | null;
    chipIconClass: string;
    chipColor: ChipProps["color"];
    tooltipText: string;
    onClickEventName: string;
    onUpgradeModalEventName: string;
    upgradeModalFsrcText: string;
    className?: string;
    onClick?: () => void;
}

function BaseFeatureIndicator({
    featureId,
    showTextInChip,
    chipTextContent,
    chipIconClass,
    chipColor,
    tooltipText,
    onClickEventName,
    onUpgradeModalEventName,
    upgradeModalFsrcText,
    className,
    onClick
}: BaseFeatureIndicatorProps) {
    const { open: openModal, close: closeModal } = usePremiumModalStore.getState();

    const handleChipClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        onClick?.();

        const handleOpenUpgradeModal = () => {
            (window as any)?.eventTracker?.event({
                type: onUpgradeModalEventName,
                data: { feature: featureId }
            });
            // Close current modal and open upgrade modal
            closeModal();
            openModal(
                <UpgradeModal
                    isModalOpen={true}
                    closeModal={closeModal}
                    fsrc={upgradeModalFsrcText}
                    className="z-[3000]"
                />
            );
        };

        openModal(
            <TrialLearnMoreDialog
                isOpen={true}
                onClose={closeModal}
                handleSelectPlan={handleOpenUpgradeModal}
                modalClassName="z-[2000]"
                feature={featureId}
                source="trial_indicator"
            />
        );
    };

    return (
        <TooltipContainer
            type="titleOnly"
            title={tooltipText}
            customButton={
                <div className={twClassNames(className, "flex items-center justify-center")}>
                    <Chip
                        color={chipColor}
                        type={showTextInChip ? "text" : "square"}
                        onClick={handleChipClick}
                        className="interactive"
                    >
                        <i className={chipIconClass} />
                        {showTextInChip && chipTextContent && <span>{chipTextContent}</span>}
                    </Chip>
                </div>
            }
        />
    );
}

type SpecificIndicatorProps = {
    featureId: Feature;
    showText: boolean;
    indicatorType: "feature" | "limit";
    className?: string;
    onClick?: () => void;
};

function AvailableTrialFeatureIndicator({
    featureId,
    showText,
    indicatorType,
    className,
    onClick
}: SpecificIndicatorProps) {
    const tooltipText =
        indicatorType == "feature"
            ? "This paid feature is available on your trial. Click to learn more."
            : "This feature has expanded limits during your trial.";

    return (
        <BaseFeatureIndicator
            featureId={featureId}
            showTextInChip={showText}
            chipTextContent="Trial"
            chipIconClass={getIconForWorkspacePlan(Plan.UbpPremiumTrial)}
            chipColor="purple"
            tooltipText={tooltipText}
            onClickEventName="Premium Trial Feature Available Indicator Clicked"
            onUpgradeModalEventName="Trial Modal Upgraded"
            upgradeModalFsrcText={`Trial feature available indicator`}
            className={className}
            onClick={onClick}
        />
    );
}

function UnavailableTrialFeatureIndicator({
    featureId,
    showText,
    indicatorType,
    className,
    onClick,
    tooltipTitle
}: SpecificIndicatorProps & { tooltipTitle?: string }) {
    return (
        <BaseFeatureIndicator
            featureId={featureId}
            showTextInChip={showText}
            chipTextContent="Trial"
            chipIconClass="fas fa-lock"
            chipColor="yellow"
            tooltipText={
                tooltipTitle ??
                "This feature is not available on the free trial. Click to subscribe to a paid plan."
            }
            onClickEventName="Premium Trial Feature Unavailable Indicator Clicked"
            onUpgradeModalEventName="Trial Upgrade Clicked"
            upgradeModalFsrcText={`Trial feature unavailable indicator`}
            className={className}
            onClick={onClick}
        />
    );
}

function AvailablePaidFeatureIndicator({
    featureId,
    showText,
    indicatorType,
    className,
    onClick
}: SpecificIndicatorProps) {
    return null;
}

function UpsellFeatureIndicator({
    featureId,
    showText,
    indicatorType,
    className,
    onClick,
    tooltipTitle
}: SpecificIndicatorProps & { tooltipTitle?: string }) {
    const handleClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        onClick?.();
        (window as any)?.eventTracker?.event({
            type: "Upsell Indicator Clicked",
            data: { feature: featureId }
        });
        openUpsellFeatureModal(featureId);
    };

    return (
        <TooltipContainer
            type="titleOnly"
            title={tooltipTitle ?? "Upgrade your plan to unlock this feature."}
            customButton={
                <div className={twClassNames(className, "flex items-center justify-center")}>
                    <Chip
                        color="yellow"
                        type={showText ? "text" : "square"}
                        onClick={handleClick}
                        className="cursor-pointer"
                    >
                        <i className="fas fa-lock" />
                        {showText && <span>Upgrade</span>}
                    </Chip>
                </div>
            }
        />
    );
}

type PremiumIndicatorProps = {
    featureId?: Feature;
    showText?: boolean;
    indicatorType?: "feature" | "limit";
    className?: string;
    tooltipTitle?: string;
    // Data attributes when used with data-react (kebab-case becomes camelCase)
    dataFeatureId?: string;
    dataShowText?: string;
    dataIndicatorType?: string;
    disableUpsellIndicator?: boolean;
    onClick?: () => void;
};

export function PremiumIndicator({
    featureId: propFeatureId,
    showText: propShowText,
    indicatorType: propIndicatorType,
    className = "",
    tooltipTitle,
    disableUpsellIndicator = false,
    onClick,
    // Data attributes from data-react usage
    ...props
}: PremiumIndicatorProps) {
    // Use prop values or data attribute values
    const rawFeatureId = props?.dataFeatureId || propFeatureId || "";
    const featureId = isValidFeatureId(rawFeatureId) ? (rawFeatureId as Feature) : null;
    const showText = props?.dataShowText === "true" || propShowText || false;
    const indicatorType = props?.dataIndicatorType || propIndicatorType || "feature";

    disableUpsellIndicator =
        typeof disableUpsellIndicator === "string"
            ? disableUpsellIndicator === "true"
            : disableUpsellIndicator;

    // Guards
    if (!isValidFeatureId(rawFeatureId)) {
        throw new Error(
            `Invalid featureId: ${featureId}. Either add to featureAvailability or fix to use the appropriate ID`
        );
    }
    if (indicatorType !== "feature" && indicatorType !== "limit") {
        throw new Error(
            `Invalid indicatorType: ${indicatorType}. Must be either "feature" or "limit"`
        );
    }

    const { usage } = useStore(globalStore, (state) => ({ usage: state.getUsage() }));

    const trialFeature = isTrialFeature(featureId);
    const premiumFeature = isPremiumFeature(featureId);

    const planIsTrial = isTrial(usage);
    const featureAvailableInCurrentPlan = isFeatureAvailable(featureId, usage);

    const upsellableFeature = isUpsellableFeature(featureId);

    if (planIsTrial) {
        if (featureAvailableInCurrentPlan) {
            // Feature is available on trial
            return (
                <AvailableTrialFeatureIndicator
                    featureId={featureId}
                    showText={showText}
                    indicatorType={indicatorType}
                    className={className}
                    onClick={onClick}
                />
            );
        } else if (upsellableFeature) {
            // Upsellable feature, but not available on trial
            return (
                <UnavailableTrialFeatureIndicator
                    featureId={featureId}
                    showText={showText}
                    indicatorType={indicatorType}
                    className={className}
                    onClick={onClick}
                    tooltipTitle={tooltipTitle}
                />
            );
        }
        // TODO: Make a indicator for enterprise features
        return null;
    } else if (featureAvailableInCurrentPlan) {
        // Available in their non-trial plan
        // Potential to show "This paid feature is available on your plan. Learn more"
        // Currently a stub, nothing shown
        return (
            <AvailablePaidFeatureIndicator
                featureId={featureId}
                showText={showText}
                indicatorType={indicatorType}
                className={className}
                onClick={onClick}
            />
        );
    } else if (upsellableFeature) {
        // Upsellable feature, but not available in current plan
        if (disableUpsellIndicator) return null;
        return (
            <UpsellFeatureIndicator
                featureId={featureId}
                showText={showText}
                indicatorType={indicatorType}
                className={className}
                onClick={onClick}
                tooltipTitle={tooltipTitle}
            />
        );
    } else {
        // Not available in any available plan
        return null;
    }
}
